import { useState, useRef } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";

const moveSizes = [
  { value: "1", label: "Studio" },
  { value: "2", label: "1 Bedroom" },
  { value: "3", label: "2 Bedrooms" },
  { value: "4", label: "3 Bedrooms" },
  { value: "5", label: "4 Bedrooms" },
  { value: "6", label: "4+ Bedrooms" },
  { value: "7", label: "Small Office" },
  { value: "8", label: "Medium Office" },
  { value: "9", label: "Large Office" },
  { value: "10", label: "Small storage (5x5, 5x8, 5x10)" },
  { value: "11", label: "Medium storage (10x10, 10x15)" },
  { value: "12", label: "Large storage (10x20)" },
];

export default function ContactUsPage() {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleNext = () => {
    const form = formRef.current;
    if (!form) return;
    const step1Fields = ["first_name", "source_zip", "destination_zip", "move_date"];
    let valid = true;
    step1Fields.forEach((name) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | null;
      if (!el || !el.value.trim()) {
        valid = false;
        el?.classList.add("border-red-500");
      } else {
        el?.classList.remove("border-red-500");
      }
    });
    if (!valid) return;
    setStep(2);
  };

  const handlePrev = () => {
    setStep(1);
    setStatus("idle");
    setErrorMsg("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = form.elements.namedItem("website_alt") as HTMLInputElement;
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    // Validate step 2 fields
    const step2Fields = ["email", "phone", "move_size_id"];
    let valid = true;
    step2Fields.forEach((name) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
      if (!el || !el.value.trim() || el.value === "default") {
        valid = false;
        el?.classList.add("border-red-500");
      } else {
        el?.classList.remove("border-red-500");
      }
    });
    if (!valid) return;

    setStatus("submitting");

    const formData = new FormData(form);
    // Remove honeypot from payload
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const response = await fetch("", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText;

      if (
        !response.ok ||
        !parsed ||
        parsed.code !== "OK" ||
        serverMsg.toLowerCase().includes("spam")
      ) {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
      setStep(1);
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 pt-20">
        {/* Contact Info Cards */}
        <section className="w-full px-4 md:px-8 lg:px-16 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {/* Address */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-[#1bb0ce] flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center text-[#1bb0ce]">
                    <i className="ri-map-pin-line text-2xl" />
                  </div>
                </div>
                <h4 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-2">
                  ADDRESS
                </h4>
                <p className="text-sm text-foreground-600">
                  <a
                    href="https://www.google.com/maps/place/NW+Trucking+of+NC+LLC/@35.2199,-80.1189,14z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#1bb0ce] transition-colors"
                  >
                    NW Trucking of NC
                  </a>
                </p>
              </div>

              {/* Phone */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-[#1bb0ce] flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center text-[#1bb0ce]">
                    <i className="ri-smartphone-line text-2xl" />
                  </div>
                </div>
                <h4 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-2">
                  PHONE
                </h4>
                <p className="text-sm text-foreground-600">
                  <a
                    href="tel:2295087357"
                    className="hover:text-[#1bb0ce] transition-colors"
                  >
                    (229) 508-7357
                  </a>
                </p>
              </div>

              {/* Working Hours */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-[#1bb0ce] flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center text-[#1bb0ce]">
                    <i className="ri-time-line text-2xl" />
                  </div>
                </div>
                <h4 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-2">
                  WORKING
                </h4>
                <p className="text-sm text-foreground-600">
                  Monday - Sunday 07.00 - 20.00
                </p>
              </div>

              {/* Email */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-[#1bb0ce] flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center text-[#1bb0ce]">
                    <i className="ri-mail-line text-2xl" />
                  </div>
                </div>
                <h4 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-2">
                  EMAIL ADDRESS
                </h4>
                <p className="text-sm text-foreground-600">
                  <a
                    href="mailto:info@nwtrucking.us"
                    className="hover:text-[#1bb0ce] transition-colors"
                  >
                    info@nwtrucking.us
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Us Heading + Form */}
        <section className="w-full px-4 md:px-8 lg:px-16 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto">
            {/* Heading */}
            <div className="mb-10">
              <h2 className="text-xs font-bold tracking-[0.3em] text-foreground-900 uppercase mb-4">
                CONTACT US
              </h2>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                <span>If you are interested in talking </span>
                <span className="text-[#1bb0ce]">to us about a project, </span>
                <span>please </span>
                <span className="text-[#1bb0ce]">send us a message</span>
              </h3>
            </div>

            {/* Form */}
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 text-center mb-6 md:mb-8">
                Get a Free Quote!
              </h2>

              {status === "success" && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm text-center">
                  Thank you! Your quote request has been submitted successfully.
                </div>
              )}

              {status === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm text-center">
                  {errorMsg}
                </div>
              )}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-6"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                {/* Step 1 */}
                <div className={step === 1 ? "block" : "hidden"}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-user-line text-sm" />
                      </div>
                      <input
                        type="text"
                        name="first_name"
                        placeholder="First and Last Name*"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-home-4-line text-sm" />
                      </div>
                      <input
                        type="text"
                        name="source_zip"
                        maxLength={5}
                        placeholder="Moving From ZIP code*"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-map-pin-line text-sm" />
                      </div>
                      <input
                        type="text"
                        name="destination_zip"
                        maxLength={5}
                        placeholder="Moving To ZIP code*"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-calendar-line text-sm" />
                      </div>
                      <input
                        type="date"
                        name="move_date"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                        onChange={(e) => {
                          e.target.classList.toggle("has-value", !!e.target.value);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className={step === 2 ? "block" : "hidden"}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-mail-line text-sm" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your Email Address*"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#1bb0ce]">
                        <i className="ri-phone-line text-sm" />
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Your Phone Number*"
                        required
                        className="w-full pl-10 pr-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                    <div className="relative">
                      <select
                        name="move_size_id"
                        required
                        defaultValue="default"
                        className="w-full px-4 py-3 text-sm border border-background-300 rounded-md focus:outline-none focus:border-[#1bb0ce] focus:ring-1 focus:ring-[#1bb0ce] bg-background-50 text-foreground-900 appearance-none"
                      >
                        <option value="default" disabled>
                          Size Of Move*
                        </option>
                        {moveSizes.map((size) => (
                          <option key={size.value} value={size.value}>
                            {size.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
                        <i className="ri-arrow-down-s-line" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <button
                      type="button"
                      onClick={handlePrev}
                      disabled={step === 1}
                      className="px-6 py-2.5 text-sm font-medium rounded-md border border-background-300 text-foreground-500 bg-background-100 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-background-200 transition-colors whitespace-nowrap"
                    >
                      Previous
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    {step === 1 && (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="px-8 py-2.5 text-sm font-medium rounded-md bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap"
                      >
                        Next
                      </button>
                    )}
                    {step === 2 && (
                      <button
                        type="submit"
                        disabled={status === "submitting"}
                        className="px-8 py-2.5 text-sm font-medium rounded-md bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-60 whitespace-nowrap"
                      >
                        {status === "submitting" ? "Submitting..." : "Submit"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}